import React, { useState } from 'react'
import { ActivityIndicator, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import type { NativeStackScreenProps } from '@react-navigation/native-stack'
import { Button, Icon, Switch, Text, TextInput } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { type AdminOrganizerStatus, useAdminOrganizers } from '@hooks'
import type { AdminStackParamList } from '../../../navigation/navigator/AdminStackNavigator'
import { AdminPage } from './components/AdminPage'
import { PersonAvatar } from './components/PersonAvatar'

type Props = NativeStackScreenProps<AdminStackParamList, 'AdminOrganizers'>

/** Validated organizers, searchable; tap one to set its commission. */
export const AdminOrganizers: React.FC<Props> = ({ navigation, route }) => {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const styles = getStyles(theme)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<AdminOrganizerStatus>(
    route.params?.status === 'removed' ? 'removed' : 'active',
  )
  const { data, isLoading } = useAdminOrganizers(search.trim(), status)
  const organizers = data?.data ?? []
  const locale = currentLanguage === 'en' ? 'en-GB' : currentLanguage === 'es' ? 'es-ES' : 'fr-FR'

  return (
    <AdminPage
      title={t('admin.organizers.title')}
      onBack={() => navigation.navigate('AdminHome')}
      active="organizers"
    >
      <Button
        testID="admin-add-organizer"
        title={t('admin.organizers.add')}
        iconName="plus"
        iconColor={theme.colors.white}
        isSmall
        onPress={() => navigation.navigate('AdminAddOrganizer')}
      />
      <View style={styles.switch}>
        <Switch
          left={{ label: t('admin.organizers.active'), value: 'active' }}
          right={{ label: t('admin.organizers.removed'), value: 'removed' }}
          value={status}
          onChange={(value) => setStatus(value as AdminOrganizerStatus)}
        />
      </View>
      <TextInput
        testID="admin-organizers-search"
        iconLeft="search"
        placeholder={t('admin.organizers.searchPlaceholder')}
        value={search}
        onChangeText={setSearch}
        autoCapitalize="none"
      />
      {isLoading ? (
        <ActivityIndicator color={theme.colors.primary} />
      ) : organizers.length === 0 ? (
        <View style={styles.empty} testID="admin-organizers-empty">
          <Text type="subtitleBold" center>
            {t(status === 'removed' ? 'admin.organizers.emptyRemoved' : 'admin.organizers.empty')}
          </Text>
        </View>
      ) : (
        organizers.map((organizer) => (
          <TouchableOpacity
            key={organizer.accountId}
            accessibilityRole="button"
            testID={`admin-organizer-${organizer.accountId}`}
            style={styles.row}
            onPress={() =>
              navigation.navigate('AdminOrganizerDetail', { accountId: organizer.accountId })
            }
          >
            <PersonAvatar
              uri={organizer.avatarUrl}
              firstName={organizer.firstName}
              lastName={organizer.lastName}
            />
            <View style={styles.body}>
              <Text type="subtitleBold" numberOfLines={1}>
                {`${organizer.firstName} ${organizer.lastName}`.trim()}
              </Text>
              {organizer.organizationName ? (
                <Text type="bodySmallBold" color={theme.colors.primary} numberOfLines={1}>
                  {organizer.organizationName}
                </Text>
              ) : null}
              <Text type="bodySmall" color={theme.colors.greyBlue} numberOfLines={1}>
                {organizer.email}
              </Text>
              <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
                {[
                  t('admin.organizers.eventCount', { count: organizer.eventCount }),
                  organizer.validatedAt
                    ? t('admin.organizers.validatedOn', {
                        date: new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(
                          new Date(organizer.validatedAt),
                        ),
                      })
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Text>
            </View>
            <Icon name="arrowRight" size={18} color={theme.colors.greyBlue} />
          </TouchableOpacity>
        ))
      )}
    </AdminPage>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    switch: { alignSelf: 'flex-start', width: 320, maxWidth: '100%' },
    empty: { alignItems: 'center', paddingVertical: theme.spacing.xxxl * 2 },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.lg,
      padding: theme.spacing.lg,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.grey,
    },
    body: { flex: 1, minWidth: 0, gap: 2 },
  })
